/* Phase 10.6.4 · Admin — Denominator Drift Watcher panel */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./AdminMusicalIntelligenceCanary.css";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  PASS:                    { label: "PASS",                    color: "#0e7a2c", bg: "#dff4e2" },
  AWAITING_BASELINE:       { label: "AWAITING BASELINE",       color: "#8a5a00", bg: "#fdf3d8" },
  EXPECTED_CHANGE:         { label: "EXPECTED CHANGE",         color: "#5a4fcf", bg: "#e6e2fb" },
  UNEXPLAINED_DRIFT:       { label: "UNEXPLAINED DRIFT",       color: "#a63232", bg: "#fbe4e4" },
  RECONCILIATION_FAILURE:  { label: "RECONCILIATION FAILURE",  color: "#7a0e0e", bg: "#f9c8c8" },
};


function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status || "UNKNOWN", color: "#333", bg: "#eee" };
  return (
    <span data-testid={`drift-status-${status}`}
          style={{ background: meta.bg, color: meta.color,
                   padding: "4px 10px", borderRadius: 6, fontWeight: 700,
                   fontSize: 12, letterSpacing: 0.4 }}>
      {meta.label}
    </span>
  );
}


export default function AdminDenominatorDrift() {
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const [approving, setApproving] = useState(false);
  const [err, setErr] = useState("");
  const [approvalForm, setApprovalForm] = useState({
    reason_for_change:   "",
    category:            "",
    owner_notes:         "",
    developer_summary:   "",
    expected_effect:     "",
    linked_pull_request: "",
    linked_release:      "",
    notes:               "",
  });
  const setApprovalField = (k, v) => setApprovalForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        fetch(`${API}/api/admin/musical-intelligence/drift/status`,
              { credentials: "include" }),
        fetch(`${API}/api/admin/musical-intelligence/drift/runs?limit=25`,
              { credentials: "include" }),
      ]);
      if (s.ok) setStatus(await s.json());
      if (r.ok) { const j = await r.json(); setRuns(j.runs || []); }
    } catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async (mode) => {
    setRunning(true); setErr("");
    try {
      const res = await fetch(`${API}/api/admin/musical-intelligence/drift/run`,
        { method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ mode, send_notifications: false }) });
      if (!res.ok) throw new Error(await res.text());
      await load();
    } catch (e) { setErr(String(e.message)); }
    finally { setRunning(false); }
  };

  const approveBaseline = async (runId) => {
    setApproving(true); setErr("");
    try {
      const body = { run_id: runId, ...approvalForm };
      const res = await fetch(`${API}/api/admin/musical-intelligence/drift/baseline`,
        { method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body) });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        const detail = errJson?.detail || errJson;
        throw new Error(
          typeof detail === "string"
            ? detail
            : (detail?.errors || []).join(" · ") || JSON.stringify(detail));
      }
      setApprovalForm({
        reason_for_change: "", category: "", owner_notes: "",
        developer_summary: "", expected_effect: "",
        linked_pull_request: "", linked_release: "", notes: "",
      });
      await load();
    } catch (e) { setErr(String(e.message)); }
    finally { setApproving(false); }
  };

  const resolveAlert = async (alertId) => {
    try {
      await fetch(`${API}/api/admin/musical-intelligence/drift/alerts/${alertId}/resolve`,
        { method: "POST", credentials: "include" });
      await load();
    } catch (e) { setErr(String(e.message)); }
  };

  const baseline    = status?.baseline;
  const latest      = status?.latest_check;
  const latestRec   = latest?.reconciliation || {};
  const currentProof= latestRec?.proof || "—";
  const baselineProof = baseline?.reconciliation_proof || "—";
  const flags       = status?.flags || {};
  const openAlerts  = status?.open_alerts || [];
  const latestStatus= latest?.status || (baseline ? "—" : "AWAITING_BASELINE");

  const changedStates = useMemo(() => {
    const d = latest?.diff_vs_baseline || {};
    return Object.entries(d)
      .filter(([k, v]) => k !== "_totals" && v && v.delta !== undefined && v.delta !== 0)
      .map(([k, v]) => ({ state: k, ...v }));
  }, [latest]);

  return (
    <div className="mi-canary" data-testid="admin-denominator-drift">
      <header className="mi-canary-header">
        <div>
          <h1>Denominator Drift Watcher</h1>
          <p className="mi-canary-sub">
            Deterministic nightly reconciliation gate for Musical Intelligence™.{" "}
            <span style={{ color: "#8a5a00" }}>MI mode: <strong>{flags.MI_MODE}</strong></span>
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="mi-run"
                  data-testid="drift-run-dry-run"
                  disabled={running}
                  onClick={() => runNow("dry_run")}>
            {running ? "Running…" : "Run dry-run now"}
          </button>
          <button className="mi-run"
                  data-testid="drift-run-full-canary"
                  disabled={running}
                  onClick={() => runNow("full_canary")}>
            Run full canary
          </button>
        </div>
      </header>

      {err && <div className="mi-canary-error" data-testid="drift-error">{err}</div>}

      {/* Status card */}
      <section className="mi-canary-panel" data-testid="drift-summary-panel"
               style={{ borderTop: `4px solid ${(STATUS_META[latestStatus] || {color:"#333"}).color}` }}>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between",
                       alignItems: "center", gap: 12 }}>
          <div>
            <h2 style={{ margin: 0 }}>Current status</h2>
            <div style={{ marginTop: 6 }}><StatusPill status={latestStatus} /></div>
          </div>
          <div style={{ textAlign: "right", fontSize: 13, color: "#444" }}>
            <div>Schema: <code>{status?.order_of_service_schema_version}</code></div>
            <div>Profile registry: <code>{status?.profile_registry_version}</code></div>
            <div>Canary matrix: <code>{status?.canary_matrix_version}</code></div>
            <div>Last run: {latest?.completed_at ? new Date(latest.completed_at).toLocaleString() : "never"}</div>
          </div>
        </div>

        <div style={{ display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                      gap: 12, marginTop: 14 }}>
          <div><span>Current proof</span>
               <strong data-testid="drift-current-proof">{currentProof}</strong></div>
          <div><span>Baseline proof</span>
               <strong data-testid="drift-baseline-proof">{baselineProof}</strong></div>
          <div><span>Baseline ID</span>
               <strong data-testid="drift-baseline-id">{baseline?.baseline_id || "—"}</strong></div>
          <div><span>Approved by</span>
               <strong>{baseline?.approved_by || "—"}</strong></div>
        </div>

        {/* Flags */}
        <div style={{ marginTop: 12, fontSize: 12, color: "#666" }}>
          <span>DRIFT_WATCHER_ENABLED=<b>{flags.DRIFT_WATCHER_ENABLED}</b></span>{" · "}
          <span>DRIFT_WATCHER_HOUR_UTC=<b>{flags.DRIFT_WATCHER_HOUR_UTC}</b></span>{" · "}
          <span>WEEKLY_CANARY_ENABLED=<b>{flags.WEEKLY_CANARY_ENABLED}</b></span>{" · "}
          <span>DRIFT_ALERT_RECIPIENT=<b>{String(flags.DRIFT_ALERT_RECIPIENT)}</b></span>
        </div>
      </section>

      {/* Diff */}
      {baseline && latest && (
        <section className="mi-canary-panel" data-testid="drift-snapshot-panel">
          <h2>Complete baseline snapshot</h2>
          <p style={{ fontSize: 13, color: "#666" }}>
            A baseline is a complete snapshot of Musical Intelligence™ —
            9 provenance fingerprints identify every rule and catalog that
            produced the ledger counts.
          </p>
          <table className="mi-canary-table"
                 data-testid="drift-snapshot-table">
            <thead>
              <tr>
                <th>Field</th><th>Baseline</th><th>Current</th><th></th>
              </tr>
            </thead>
            <tbody>
              {[
                ["order_of_service_schema_version","Schema version"],
                ["order_of_service_registry_hash","Order-of-Service registry"],
                ["applicability_rule_hash","Applicability rule"],
                ["conditional_rule_hash","Conditional rule"],
                ["recommendation_engine_version","Recommendation engine"],
                ["hymnal_catalog_version","Hymnal catalog"],
                ["repertoire_version","Repertoire"],
                ["canary_matrix_version","Canary matrix"],
                ["seasonal_rule_version","Seasonal rule"],
              ].map(([key, label]) => {
                const b = baseline[key] || "—";
                const c = (status?.current_snapshot || {})[key] || "—";
                const match = b === c;
                return (
                  <tr key={key} data-testid={`drift-snap-row-${key}`}>
                    <td>{label}</td>
                    <td style={{ fontFamily: "monospace", fontSize: 11 }}>{b}</td>
                    <td style={{ fontFamily: "monospace", fontSize: 11,
                                  color: match ? "#0e7a2c" : "#a63232",
                                  fontWeight: match ? 400 : 700 }}>{c}</td>
                    <td>{match ? "✅" : "⚠"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {baseline && (
        <section className="mi-canary-panel" data-testid="drift-baseline-provenance">
          <h2>Baseline provenance</h2>
          <div style={{ display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                        gap: 12 }}>
            <div><span>Baseline ID</span>
                 <strong data-testid="drift-provenance-id">{baseline.baseline_id}</strong></div>
            <div><span>Approved by</span>
                 <strong>{baseline.approved_by}</strong></div>
            <div><span>Approval timestamp</span>
                 <strong>{baseline.approval_timestamp
                          ? new Date(baseline.approval_timestamp).toLocaleString()
                          : "—"}</strong></div>
            <div><span>Category</span>
                 <strong data-testid="drift-provenance-category">
                   {baseline.category || "—"}
                 </strong></div>
            <div style={{ gridColumn: "1 / -1" }}>
              <span>Reason for change</span>
              <div data-testid="drift-provenance-reason">
                {baseline.reason_for_change || "—"}
              </div>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <span>Owner notes</span>
              <div>{baseline.owner_notes || "—"}</div>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <span>Developer summary</span>
              <div>{baseline.developer_summary || "—"}</div>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <span>Expected effect</span>
              <div>{baseline.expected_effect || "—"}</div>
            </div>
            <div>
              <span>Linked PR</span>
              {baseline.linked_pull_request
                ? <a href={baseline.linked_pull_request} target="_blank" rel="noreferrer">
                    {baseline.linked_pull_request}</a>
                : "—"}
            </div>
            <div>
              <span>Linked release</span>
              <strong>{baseline.linked_release || "—"}</strong>
            </div>
          </div>
        </section>
      )}

      {baseline && latest && (
        <section className="mi-canary-panel" data-testid="drift-diff-panel">
          <h2>Ledger count diff vs baseline</h2>
          {changedStates.length === 0 ? (
            <p data-testid="drift-no-changes">
              ✅ No count changes vs baseline. Proof and every applicability bucket are identical.
            </p>
          ) : (
            <table className="mi-canary-table">
              <thead>
                <tr><th>State</th><th>Baseline</th><th>Current</th><th>Δ</th><th>%</th></tr>
              </thead>
              <tbody>
                {changedStates.map((c) => (
                  <tr key={c.state} data-testid={`drift-change-${c.state}`}>
                    <td>{c.state}</td>
                    <td>{c.baseline}</td>
                    <td>{c.current}</td>
                    <td style={{ color: c.delta > 0 ? "#0e7a2c" : "#a63232" }}>
                      {c.delta > 0 ? "+" : ""}{c.delta}
                    </td>
                    <td>{isFinite(c.pct_change) ? `${c.pct_change}%` : "n/a"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {/* Alerts */}
      {openAlerts.length > 0 && (
        <section className="mi-canary-panel" data-testid="drift-alerts-panel"
                 style={{ borderTop: "4px solid #a63232" }}>
          <h2>Open alerts ({openAlerts.length})</h2>
          <table className="mi-canary-table">
            <thead>
              <tr>
                <th>Status</th><th>Occurrences</th><th>First seen</th>
                <th>Last seen</th><th>Email</th><th></th>
              </tr>
            </thead>
            <tbody>
              {openAlerts.map((a) => (
                <tr key={a.alert_id} data-testid={`drift-alert-${a.alert_id}`}>
                  <td><StatusPill status={a.status} /></td>
                  <td>{a.occurrence_count}</td>
                  <td>{new Date(a.created_at).toLocaleString()}</td>
                  <td>{new Date(a.last_seen_at).toLocaleString()}</td>
                  <td style={{ fontSize: 11 }}>{a.email_status}</td>
                  <td>
                    <button className="mi-run" onClick={() => resolveAlert(a.alert_id)}
                            data-testid={`drift-resolve-${a.alert_id}`}>Resolve</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Baseline approval */}
      {latest && latest.identity_ok && (
        <section className="mi-canary-panel" data-testid="drift-approve-panel">
          <h2>Approve new baseline</h2>
          <p style={{ fontSize: 13, color: "#666" }}>
            Baseline replacement is <strong>never automatic</strong>. Owner review required.
            All five required fields must be filled — this record permanently answers <em>why</em> the denominator changed.
          </p>
          <div style={{ display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                        gap: 10 }}>
            <div>
              <label style={{ display: "block", fontSize: 12, color: "#333",
                              fontWeight: 600 }}>
                Reason for change <span style={{ color: "#a63232" }}>*</span>
              </label>
              <input className="mi-canary-input" style={{ width: "100%" }}
                     value={approvalForm.reason_for_change}
                     onChange={(e) => setApprovalField("reason_for_change", e.target.value)}
                     placeholder="e.g. Added Roman-Catholic nuptial-mass profile"
                     data-testid="drift-approval-reason" />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, color: "#333",
                              fontWeight: 600 }}>
                Category <span style={{ color: "#a63232" }}>*</span>
              </label>
              <select className="mi-canary-input" style={{ width: "100%" }}
                      value={approvalForm.category}
                      onChange={(e) => setApprovalField("category", e.target.value)}
                      data-testid="drift-approval-category">
                <option value="">— select —</option>
                {(status?.baseline_categories || []).map((c) =>
                  <option key={c} value={c}>{c.replaceAll("_", " ")}</option>
                )}
              </select>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ display: "block", fontSize: 12, color: "#333",
                              fontWeight: 600 }}>
                Owner notes <span style={{ color: "#a63232" }}>*</span>
              </label>
              <textarea className="mi-canary-input"
                        style={{ width: "100%", minHeight: 60 }}
                        value={approvalForm.owner_notes}
                        onChange={(e) => setApprovalField("owner_notes", e.target.value)}
                        placeholder="Business context — why this change is expected."
                        data-testid="drift-approval-owner-notes" />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ display: "block", fontSize: 12, color: "#333",
                              fontWeight: 600 }}>
                Developer summary <span style={{ color: "#a63232" }}>*</span>
              </label>
              <textarea className="mi-canary-input"
                        style={{ width: "100%", minHeight: 60 }}
                        value={approvalForm.developer_summary}
                        onChange={(e) => setApprovalField("developer_summary", e.target.value)}
                        placeholder="Technical summary — what code / data changed."
                        data-testid="drift-approval-developer-summary" />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ display: "block", fontSize: 12, color: "#333",
                              fontWeight: 600 }}>
                Expected effect <span style={{ color: "#a63232" }}>*</span>
              </label>
              <textarea className="mi-canary-input"
                        style={{ width: "100%", minHeight: 50 }}
                        value={approvalForm.expected_effect}
                        onChange={(e) => setApprovalField("expected_effect", e.target.value)}
                        placeholder="Predicted count change (e.g. +8 REQUIRED / +2 CONDITIONAL_ACTIVE)."
                        data-testid="drift-approval-expected-effect" />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, color: "#333" }}>
                Linked pull request
              </label>
              <input className="mi-canary-input" style={{ width: "100%" }}
                     value={approvalForm.linked_pull_request}
                     onChange={(e) => setApprovalField("linked_pull_request", e.target.value)}
                     placeholder="https://github.com/…"
                     data-testid="drift-approval-linked-pr" />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, color: "#333" }}>
                Linked release
              </label>
              <input className="mi-canary-input" style={{ width: "100%" }}
                     value={approvalForm.linked_release}
                     onChange={(e) => setApprovalField("linked_release", e.target.value)}
                     placeholder="v1.6.0"
                     data-testid="drift-approval-linked-release" />
            </div>
          </div>
          <button className="mi-run"
                  data-testid="drift-approve-baseline"
                  disabled={approving}
                  onClick={() => approveBaseline(latest.run_id)}
                  style={{ marginTop: 12 }}>
            {approving ? "Approving…" : "Approve latest run as baseline"}
          </button>
        </section>
      )}

      {/* Recent runs */}
      <section className="mi-canary-panel" data-testid="drift-runs-panel">
        <h2>Recent drift checks</h2>
        <table className="mi-canary-table">
          <thead>
            <tr>
              <th>Time</th><th>Mode</th><th>Status</th><th>Proof</th>
              <th>Trigger</th><th>Ledger run</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.run_id} data-testid={`drift-run-${r.run_id}`}>
                <td>{new Date(r.completed_at || r.started_at).toLocaleString()}</td>
                <td>{r.mode}</td>
                <td><StatusPill status={r.status} /></td>
                <td style={{ fontFamily: "monospace", fontSize: 12 }}>
                  {(r.reconciliation && r.reconciliation.proof) || "—"}
                </td>
                <td style={{ fontSize: 11, color: "#666" }}>{r.triggered_by}</td>
                <td style={{ fontSize: 11 }}>
                  {r.ledger_run_id
                    ? <a href={`${API}/api/admin/musical-intelligence/drift/runs/${r.run_id}/ledger`}
                         target="_blank" rel="noreferrer">ledger</a>
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
